<template>
  <div class="approval-inbox">
    <div class="page-header">
      <h2>내 결재함</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/approval-inbox'" />
    </div>

    <el-tabs v-model="activeTab" @tab-change="handleTabChange">
      <!-- 대기 결재 -->
      <el-tab-pane name="pending">
        <template #label>
          대기 결재
          <el-badge v-if="pendingCount > 0" :value="pendingCount" :max="99" class="tab-badge" />
        </template>
        <el-table :data="myPending" v-loading="loading" stripe @row-click="handleRowClick">
          <el-table-column prop="document_type" label="문서유형" width="120">
            <template #default="{ row }">
              <el-tag size="small">{{ getDocumentTypeLabel(row.document_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="유형" width="80">
            <template #default="{ row }">
              <el-tag :type="row.step_type === 'AGREEMENT' ? 'warning' : ''" size="small">
                {{ row.step_type === 'AGREEMENT' ? '합의' : '결재' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="document_number" label="문서번호" width="150" />
          <el-table-column prop="title" label="제목" min-width="200" />
          <el-table-column prop="requester_name" label="기안자" width="100" />
          <el-table-column label="상신일" width="110">
            <template #default="{ row }">
              {{ formatDate(row.submitted_at) }}
            </template>
          </el-table-column>
          <el-table-column label="현재단계" width="120">
            <template #default="{ row }">
              {{ row.current_step || '-' }}
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- 내가 요청한 결재 -->
      <el-tab-pane label="내가 요청한 결재" name="requested">
        <el-table :data="myRequested" v-loading="loading" stripe @row-click="handleRowClick">
          <el-table-column prop="document_type" label="문서유형" width="120">
            <template #default="{ row }">
              <el-tag size="small">{{ getDocumentTypeLabel(row.document_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="document_number" label="문서번호" width="150" />
          <el-table-column prop="title" label="제목" min-width="200" />
          <el-table-column label="상신일" width="110">
            <template #default="{ row }">
              {{ formatDate(row.submitted_at) }}
            </template>
          </el-table-column>
          <el-table-column label="상태" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ row.status_display || row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="현재단계" width="120">
            <template #default="{ row }">
              {{ row.current_step || '-' }}
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- 처리 완료 -->
      <el-tab-pane label="처리 완료" name="completed">
        <el-table :data="myCompleted" v-loading="loading" stripe @row-click="handleRowClick">
          <el-table-column prop="document_type" label="문서유형" width="120">
            <template #default="{ row }">
              <el-tag size="small">{{ getDocumentTypeLabel(row.document_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="document_number" label="문서번호" width="150" />
          <el-table-column prop="title" label="제목" min-width="200" />
          <el-table-column prop="requester_name" label="기안자" width="100" />
          <el-table-column label="상신일" width="110">
            <template #default="{ row }">
              {{ formatDate(row.submitted_at) }}
            </template>
          </el-table-column>
          <el-table-column label="완료일" width="110">
            <template #default="{ row }">
              {{ formatDate(row.completed_at) }}
            </template>
          </el-table-column>
          <el-table-column label="상태" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ row.status_display || row.status }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- 참조 문서 -->
      <el-tab-pane label="참조 문서" name="references">
        <el-table :data="myReferences" v-loading="loading" stripe @row-click="handleRowClick">
          <el-table-column prop="document_type" label="문서유형" width="120">
            <template #default="{ row }">
              <el-tag size="small">{{ getDocumentTypeLabel(row.document_type) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="document_number" label="문서번호" width="150" />
          <el-table-column prop="title" label="제목" min-width="200" />
          <el-table-column prop="requester_name" label="기안자" width="100" />
          <el-table-column label="상신일" width="110">
            <template #default="{ row }">
              {{ formatDate(row.submitted_at) }}
            </template>
          </el-table-column>
          <el-table-column label="상태" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small">
                {{ row.status_display || row.status }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <!-- 공용 결재 다이얼로그 — document_summary 스냅샷 기반 (현재: 휴가) -->
    <ApprovalProcessDialog
      v-model="dialogVisible"
      :approval-id="selectedApprovalId"
      :allow-process="activeTab === 'pending'"
      :detail-path="selectedDetailPath"
      @processed="fetchData"
    />
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
const helpVisible = ref(false)
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useApprovalStore } from '@/stores/approvalStore'
import ApprovalProcessDialog from '@/components/approval/ApprovalProcessDialog.vue'

const router = useRouter()
const approvalStore = useApprovalStore()

const activeTab = ref('pending')
const loading = ref(false)

// === 공용 결재 다이얼로그 (요약 스냅샷 있는 문서) ===
// 자재발주 추가 (2026-07-19): 발주 화면엔 승인 UI가 없어 결재 처리가 불가능하던 갭 —
// MaterialOrder.get_approval_summary() 스냅샷이 이미 있어 다이얼로그로 바로 처리
// 생산지시서·원가보고 추가 (2026-07-29): 둘 다 get_approval_summary 스냅샷 보유 —
// 결재함에서 핵심 수치 보고 바로 승인, 상세는 다이얼로그의 문서 링크로 이동
// 증명서 신청 추가 (2026-08-12): 별도 상세화면이 없는 문서라 다이얼로그 방식이 필수 —
// 여기 빠져 있으면 결재함에서 행을 클릭해도 아무 반응이 없어 승인 버튼 자체를 볼 수 없다.
const dialogDocumentTypes = new Set(['leave', 'sample_request', 'daily_cash_report', 'material_order',
                                     'work_instruction', 'cost_report', 'certificate_request'])
const dialogVisible = ref(false)
const selectedApprovalId = ref(null)
const selectedDetailPath = ref('')

const openApprovalDialog = (row) => {
  selectedApprovalId.value = row.id
  const basePath = documentRoutes[row.document_type]
  selectedDetailPath.value = basePath && row.object_id ? `${basePath}/${row.object_id}` : ''
  dialogVisible.value = true
}

const myPending = computed(() => approvalStore.myPending)
const myRequested = computed(() => approvalStore.myRequested)
const myCompleted = computed(() => approvalStore.myCompleted)
const myReferences = computed(() => approvalStore.myReferences)
const pendingCount = computed(() => myPending.value.length)

const documentTypeLabels = {
  sample_request: '샘플요청',
  expense: '지출결의서',
  quotation: '견적서',
  material_order: '자재발주',
  main_request: '메인오더',
  work_instruction: '생산지시서',
  cost_report: '원가보고',
  leave: '휴가',
  daily_cash_report: '자금일보',
  certificate_request: '증명서 신청',
}

const getDocumentTypeLabel = (type) => documentTypeLabels[type] || type

const getStatusType = (status) => {
  const map = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger', CANCELLED: 'info', DRAFT: 'info' }
  return map[status] || 'info'
}

const formatDate = (dt) => {
  if (!dt) return '-'
  return dt.substring(0, 10)
}

// 문서유형별 상세 페이지 경로
const documentRoutes = {
  sample_request: '/sample-requests',
  expense: '/expenses',
  quotation: '/quotations',
  material_order: '/material-orders',
  main_request: '/main-requests',
  work_instruction: '/work-instructions',
  daily_cash_report: '/finance/daily-reports',
  // cost_report(CostComparison)의 object_id는 MR id가 아니라 라우팅 불가 —
  // 다이얼로그 요약으로 처리 (핵심 수치가 스냅샷에 포함)
}

const handleRowClick = (row) => {
  // 요약 스냅샷 기반 문서: 결재함 안에서 공용 다이얼로그로 바로 처리
  if (dialogDocumentTypes.has(row.document_type)) {
    openApprovalDialog(row)
    return
  }
  const basePath = documentRoutes[row.document_type]
  if (basePath && row.object_id) {
    router.push(`${basePath}/${row.object_id}`)
  }
}

const fetchData = async () => {
  loading.value = true
  try {
    if (activeTab.value === 'pending') {
      await approvalStore.fetchMyPending()
    } else if (activeTab.value === 'requested') {
      await approvalStore.fetchMyRequested()
    } else if (activeTab.value === 'completed') {
      await approvalStore.fetchMyCompleted()
    } else if (activeTab.value === 'references') {
      await approvalStore.fetchMyReferences()
    }
  } finally {
    loading.value = false
  }
}

const handleTabChange = () => {
  fetchData()
}

onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.approval-inbox {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.tab-badge {
  margin-left: 6px;
}

:deep(.el-table) {
  cursor: pointer;
}
</style>
